import { __decorate } from 'tslib';
import * as i0 from '@angular/core';
import { TemplateRef, EventEmitter, forwardRef, Component, ViewEncapsulation, ChangeDetectionStrategy, Inject, Input, Output, NgModule } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { InputBoolean, InputNumber } from '@ng-util/util';
import * as i3 from '@angular/common';
import { DOCUMENT, CommonModule } from '@angular/common';
import * as i2 from '@ng-util/lazy';
const _c0 = ["*"];
function TinymceComponent_textarea_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "textarea", 3);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵattribute("id", ctx_r0.id)("placeholder", ctx_r0.placeholder);
  }
}
function TinymceComponent_div_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div");
    i0.ɵɵprojection(1);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵattribute("id", ctx_r0.id);
  }
}
function TinymceComponent_div_2_ng_container_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtext(1);
    i0.ɵɵelementContainerEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate(ctx_r0._loading);
  }
}
function TinymceComponent_div_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 4);
    i0.ɵɵtemplate(1, TinymceComponent_div_2_ng_container_1_Template, 2, 1, "ng-container", 5);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngIf", ctx_r0._loading)("ngIfElse", ctx_r0._loadingTpl);
  }
}
class TinymceOptions {
  constructor() {
    /** 指定tinymce目录路径，默认：`./assets/tinymce/` */
    this.baseURL = './assets/tinymce/';
    /** 指定tinymce文件名，默认：`tinymce.min.js` */
    this.fileName = 'tinymce.min.js';
  }
}
const isSSR = !(typeof document === 'object' && !!document);
let TinymceComponent = /*#__PURE__*/(() => {
  class TinymceComponent {
    set disabled(value) {
      this._disabled = value;
      this.setDisabled();
    }
    set loading(value) {
      if (value instanceof TemplateRef) {
        this._loading = null;
        this._loadingTpl = value;
      } else {
        this._loading = value;
      }
    }
    get instance() {
      return this._instance;
    }
    _getWin() {
      return this.doc.defaultView || window;
    }
    constructor(defConfig, lazySrv, ngZone, doc, cd) {
      this.defConfig = defConfig;
      this.lazySrv = lazySrv;
      this.ngZone = ngZone;
      this.doc = doc;
      this.cd = cd;
      this.value = '';
      this.load = true;
      this.id = `_tinymce-${Math.random().toString(36).substring(2)}`;
      this.placeholder = '';
      this.inline = false;
      this._disabled = false;
      this._loading = null;
      this._loadingTpl = null;
      /** 延迟初始化 */
      this.delay = 0;
      this.ready = new EventEmitter();
    }
    initDelay() {
      if (isSSR) {
        return;
      }
      setTimeout(() => this.init(), Math.max(0, this.delay));
    }
    init() {
      const win = this._getWin();
      if (!win.tinymce) {
        throw new Error('tinymce js文件加载失败');
      }
      const {
        defConfig,
        config,
        id,
        inline
      } = this;
      if (this._instance) {
        return;
      }
      if (defConfig.baseURL) {
        let url = '' + defConfig.baseURL;
        if (url.endsWith('/')) {
          url = url.substring(0, url.length - 1);
        }
        win.tinymce.baseURL = url;
      }
      const userOptions = {
        ...defConfig.config,
        ...config
      };
      const options = {
        selector: `#` + id,
        inline,
        ...defConfig.config,
        ...config,
        setup: editor => {
          this._instance = editor;
          if (this.onChange) {
            editor.on('change keyup', () => {
              this.value = editor.getContent();
              this.ngZone.run(() => this.onChange(this.value));
            });
          }
          if (typeof userOptions.setup === 'function') {
            userOptions.setup(editor);
          }
        },
        init_instance_callback: editor => {
          if (editor && this.value) {
            editor.setContent(this.value);
          }
          this.setDisabled();
          if (typeof userOptions.init_instance_callback === 'function') {
            userOptions.init_instance_callback(editor);
          }
          this.ready.emit(this._instance);
        }
      };
      if (userOptions.auto_focus) {
        options.auto_focus = id;
      }
      this.ngZone.runOutsideAngular(() => win.tinymce.init(options));
      this.load = false;
      this.cd.detectChanges();
    }
    destroy() {
      if (!this._instance) {
        return;
      }
      this.ngZone.runOutsideAngular(() => {
        this._instance.off();
        this._instance.remove('#' + this.id);
      });
      this._instance = null;
    }
    setDisabled() {
      if (!this._instance) {
        return;
      }
      this.ngZone.runOutsideAngular(() => {
        const mode = this._disabled ? 'readonly' : 'design';
        // Compatible with 5
        if (typeof this._instance.setMode === 'function') {
          this._instance.setMode(mode);
        } else {
          this._instance.mode.set(mode);
        }
      });
    }
    ngAfterViewInit() {
      if (isSSR) {
        return;
      }
      // 已经存在对象无须进入懒加载模式
      if (this._getWin().tinymce) {
        this.initDelay();
        return;
      }
      const {
        defConfig
      } = this;
      const baseURL = defConfig && defConfig.baseURL;
      const fileName = defConfig && defConfig.fileName;
      const url = (baseURL || './assets/tinymce/') + (fileName || 'tinymce.min.js');
      this.lazySrv.monitor(url).subscribe(() => this.initDelay());
      this.lazySrv.load(url);
    }
    ngOnChanges(changes) {
      if (this._instance && changes.config) {
        this.destroy();
        this.initDelay();
      }
    }
    ngOnDestroy() {
      this.destroy();
    }
    writeValue(value) {
      // value should be NOT NULL
      this.value = value || '';
      if (this._instance) {
        this.ngZone.runOutsideAngular(() => this._instance.setContent(this.value));
      }
    }
    registerOnChange(fn) {
      this.onChange = fn;
    }
    registerOnTouched(fn) {
      this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
      this.disabled = isDisabled;
      this.setDisabled();
    }
    static {
      this.ɵfac = function TinymceComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TinymceComponent)(i0.ɵɵdirectiveInject(TinymceOptions), i0.ɵɵdirectiveInject(i2.NuLazyService), i0.ɵɵdirectiveInject(i0.NgZone), i0.ɵɵdirectiveInject(DOCUMENT), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef));
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: TinymceComponent,
        selectors: [["tinymce"]],
        inputs: {
          config: "config",
          placeholder: "placeholder",
          inline: "inline",
          disabled: "disabled",
          loading: "loading",
          delay: "delay"
        },
        outputs: {
          ready: "ready"
        },
        exportAs: ["tinymce"],
        standalone: false,
        features: [i0.ɵɵProvidersFeature([{
          provide: NG_VALUE_ACCESSOR,
          useExisting: forwardRef(() => TinymceComponent),
          multi: true
        }]), i0.ɵɵNgOnChangesFeature],
        ngContentSelectors: _c0,
        decls: 3,
        vars: 3,
        consts: [["class", "tinymce-selector", 4, "ngIf"], [4, "ngIf"], ["class", "loading", 4, "ngIf"], [1, "tinymce-selector"], [1, "loading"], [4, "ngIf", "ngIfElse"]],
        template: function TinymceComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵprojectionDef();
            i0.ɵɵtemplate(0, TinymceComponent_textarea_0_Template, 1, 2, "textarea", 0)(1, TinymceComponent_div_1_Template, 2, 1, "div", 1)(2, TinymceComponent_div_2_Template, 2, 2, "div", 2);
          }
          if (rf & 2) {
            i0.ɵɵproperty("ngIf", !ctx.inline);
            i0.ɵɵadvance();
            i0.ɵɵproperty("ngIf", ctx.inline);
            i0.ɵɵadvance();
            i0.ɵɵproperty("ngIf", ctx.load);
          }
        },
        dependencies: [i3.NgIf],
        styles: ["tinymce .tinymce-selector{display:none}\n"],
        encapsulation: 2,
        changeDetection: 0
      });
    }
  }
  __decorate([InputBoolean()], TinymceComponent.prototype, "inline", void 0);
  __decorate([InputBoolean()], TinymceComponent.prototype, "disabled", null);
  __decorate([InputNumber()], TinymceComponent.prototype, "delay", void 0);
  return TinymceComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let NgxTinymceModule = /*#__PURE__*/(() => {
  class NgxTinymceModule {
    static forRoot(options) {
      return {
        ngModule: NgxTinymceModule,
        providers: [{
          provide: TinymceOptions,
          useValue: options
        }]
      };
    }
    static {
      this.ɵfac = function NgxTinymceModule_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NgxTinymceModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: NgxTinymceModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
        imports: [CommonModule]
      });
    }
  }
  return NgxTinymceModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { NgxTinymceModule, TinymceComponent, TinymceOptions };
